import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { flattenTopics } from "../../lib/workshop";
import { toast } from "sonner";

const CHALLENGE_TYPES = [
  { value: "quick_decision", label: "Rychlá volba" },
  { value: "multi_step", label: "Vícekroková situace" },
  { value: "chat_simulation", label: "Chat simulace" },
  { value: "feed_analysis", label: "Analýza feedu" },
  { value: "sequence", label: "Seřaď postup" },
  { value: "vote", label: "Hlasování třídy" },
  { value: "reflection", label: "Reflexe" },
];

const randId = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 8)}`;

export default function CmsWorkshopEdit() {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selection, setSelection] = useState(null); // {kind:"topic"|"question", topicId, questionId?}
  const [expanded, setExpanded] = useState({}); // { [topicId]: bool }
  const [jsonDraft, setJsonDraft] = useState("");
  const [jsonError, setJsonError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/workshops/${workshopId}`);
        setWs(data);
        const firstTopic = data.topics?.[0];
        if (firstTopic) {
          setExpanded({ [firstTopic.id]: true });
          const firstQ = firstTopic.questions?.[0];
          setSelection(firstQ
            ? { kind: "question", topicId: firstTopic.id, questionId: firstQ.id }
            : { kind: "topic", topicId: firstTopic.id }
          );
        }
      } catch {
        toast.error("Workshop nenalezen");
        navigate("/teacher/cms");
      } finally {
        setLoading(false);
      }
    })();
  }, [workshopId, navigate]);

  const teacher = JSON.parse(localStorage.getItem("dm_teacher") || "{}");
  const isAdmin = teacher?.role === "admin";
  const readOnly = ws?.is_system || !isAdmin;

  const topics = ws?.topics || [];
  const flatQuestions = useMemo(() => flattenTopics(topics), [topics]);

  const currentNode = useMemo(() => {
    if (!selection || !ws) return null;
    const t = topics.find((x) => x.id === selection.topicId);
    if (!t) return null;
    if (selection.kind === "topic") return { kind: "topic", topic: t, obj: t };
    const q = (t.questions || []).find((x) => x.id === selection.questionId);
    if (!q) return null;
    return { kind: "question", topic: t, obj: q };
  }, [selection, ws, topics]);

  useEffect(() => {
    if (currentNode) {
      setJsonDraft(JSON.stringify(currentNode.obj, null, 2));
      setJsonError("");
    } else {
      setJsonDraft("");
    }
  }, [currentNode]);

  const setField = (k, v) => setWs((w) => ({ ...w, [k]: v }));

  const save = async () => {
    if (readOnly) {
      toast.error("Systémový workshop nelze upravit. Duplikuj si ho.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/workshops/${workshopId}`, {
        title: ws.title,
        subtitle: ws.subtitle,
        description: ws.description,
        age_range: ws.age_range,
        duration_minutes: ws.duration_minutes,
        difficulty: ws.difficulty,
        tags: ws.tags,
        topics: ws.topics,
      });
      setWs(data);
      toast.success("Uloženo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nepodařilo se uložit");
    } finally {
      setSaving(false);
    }
  };

  const applyJson = () => {
    if (readOnly || !currentNode) return;
    try {
      const parsed = JSON.parse(jsonDraft);
      if (!parsed.id) {
        setJsonError("Objekt musí mít id");
        return;
      }
      if (currentNode.kind === "topic") {
        if (!Array.isArray(parsed.questions)) {
          setJsonError("Téma musí mít pole questions[]");
          return;
        }
        setWs((w) => ({
          ...w,
          topics: w.topics.map((t) => (t.id === currentNode.topic.id ? parsed : t)),
        }));
      } else {
        if (!parsed.type || !parsed.title) {
          setJsonError("Otázka potřebuje id, type a title");
          return;
        }
        setWs((w) => ({
          ...w,
          topics: w.topics.map((t) =>
            t.id === currentNode.topic.id
              ? { ...t, questions: (t.questions || []).map((q) => (q.id === parsed.id ? parsed : q)) }
              : t
          ),
        }));
      }
      setJsonError("");
      toast.success("Změny aplikovány (ještě klikni Uložit workshop)");
    } catch (e) {
      setJsonError("Neplatný JSON: " + e.message);
    }
  };

  const addTopic = () => {
    if (readOnly) return;
    const id = randId("t-custom");
    const newTopic = {
      id,
      title: `Nové téma ${topics.length + 1}`,
      intro: "",
      order: topics.length + 1,
      lecturer_notes: { intro: "", discussion: [], takeaway: "" },
      questions: [],
    };
    setWs((w) => ({ ...w, topics: [...(w.topics || []), newTopic] }));
    setExpanded((e) => ({ ...e, [id]: true }));
    setSelection({ kind: "topic", topicId: id });
  };

  const removeTopic = (tid) => {
    if (readOnly) return;
    if (!window.confirm("Smazat toto téma se všemi otázkami?")) return;
    setWs((w) => ({ ...w, topics: w.topics.filter((t) => t.id !== tid) }));
    if (selection?.topicId === tid) setSelection(null);
  };

  const moveTopic = (tid, dir) => {
    if (readOnly) return;
    setWs((w) => {
      const arr = [...w.topics];
      const idx = arr.findIndex((t) => t.id === tid);
      const swapIdx = idx + dir;
      if (idx < 0 || swapIdx < 0 || swapIdx >= arr.length) return w;
      [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
      return { ...w, topics: arr.map((t, i) => ({ ...t, order: i + 1 })) };
    });
  };

  const renameTopic = (tid, newTitle) => {
    setWs((w) => ({
      ...w,
      topics: w.topics.map((t) => (t.id === tid ? { ...t, title: newTitle } : t)),
    }));
  };

  const addQuestion = (tid) => {
    if (readOnly) return;
    const id = randId("q-custom");
    const newQ = {
      id,
      type: "quick_decision",
      title: "Nová otázka",
      context: "Popis situace",
      projector_text: "",
      duration_seconds: 45,
      choices: [
        { id: "a", label: "Volba A", effects: { mood: 2 } },
        { id: "b", label: "Volba B", effects: { mood: -2 } },
      ],
    };
    setWs((w) => ({
      ...w,
      topics: w.topics.map((t) => (t.id === tid ? { ...t, questions: [...(t.questions || []), newQ] } : t)),
    }));
    setExpanded((e) => ({ ...e, [tid]: true }));
    setSelection({ kind: "question", topicId: tid, questionId: id });
  };

  const removeQuestion = (tid, qid) => {
    if (readOnly) return;
    if (!window.confirm("Smazat tuhle otázku?")) return;
    setWs((w) => ({
      ...w,
      topics: w.topics.map((t) => (t.id === tid ? { ...t, questions: t.questions.filter((q) => q.id !== qid) } : t)),
    }));
    if (selection?.questionId === qid) setSelection({ kind: "topic", topicId: tid });
  };

  const moveQuestion = (tid, qid, dir) => {
    if (readOnly) return;
    setWs((w) => ({
      ...w,
      topics: w.topics.map((t) => {
        if (t.id !== tid) return t;
        const arr = [...(t.questions || [])];
        const idx = arr.findIndex((q) => q.id === qid);
        const swapIdx = idx + dir;
        if (idx < 0 || swapIdx < 0 || swapIdx >= arr.length) return t;
        [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
        return { ...t, questions: arr };
      }),
    }));
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ schema_version: "1.0", workshop: ws }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ws.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !ws) return <div className="min-h-screen flex items-center justify-center text-slate-500">Načítám…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/teacher/cms" className="text-slate-500 hover:text-slate-900 text-sm font-bold">← CMS</Link>
            <div>
              <div className="font-heading font-black text-lg tracking-tight text-slate-900">{ws.title}</div>
              <div className="text-xs text-slate-500">
                {topics.length} téma{topics.length === 1 ? "" : topics.length < 5 ? "ta" : "t"} · {flatQuestions.length} otázka{flatQuestions.length === 1 ? "" : flatQuestions.length < 5 ? "y" : ""} · {ws.is_system ? "Jen pro čtení (duplikuj si kopii)" : "Vlastní workshop"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportJson}
              className="text-sm font-bold text-slate-600 hover:text-slate-900 border border-slate-200 rounded-md px-3 py-2"
              data-testid="btn-export-workshop"
            >
              ⤓ Export JSON
            </button>
            <button
              onClick={save}
              disabled={saving || readOnly}
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md px-4 py-2 text-sm disabled:opacity-40"
              data-testid="btn-save-workshop"
            >
              {saving ? "Ukládám…" : "Uložit workshop"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        {/* Left: workshop meta + topic tree */}
        <aside className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Základní info</div>
            <Input label="Název" value={ws.title} onChange={(v) => setField("title", v)} disabled={readOnly} />
            <Input label="Podnadpis" value={ws.subtitle} onChange={(v) => setField("subtitle", v)} disabled={readOnly} />
            <Textarea label="Popis" value={ws.description} onChange={(v) => setField("description", v)} disabled={readOnly} />
            <div className="grid grid-cols-2 gap-2">
              <Input label="Věk" value={ws.age_range} onChange={(v) => setField("age_range", v)} disabled={readOnly} />
              <Input label="Délka (min)" type="number" value={ws.duration_minutes} onChange={(v) => setField("duration_minutes", parseInt(v) || 45)} disabled={readOnly} />
            </div>
            <Input label="Tagy (čárkou)" value={(ws.tags || []).join(", ")} onChange={(v) => setField("tags", v.split(",").map((s) => s.trim()).filter(Boolean))} disabled={readOnly} />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Struktura workshopu</div>
              <button
                onClick={addTopic}
                disabled={readOnly}
                className="text-xs font-bold text-blue-600 hover:underline disabled:opacity-40"
                data-testid="btn-add-topic"
              >
                + Téma
              </button>
            </div>
            <div className="space-y-2">
              {topics.map((t, ti) => {
                const open = !!expanded[t.id];
                const selectedTopic = selection?.kind === "topic" && selection.topicId === t.id;
                return (
                  <div
                    key={t.id}
                    className={`border rounded-md ${selectedTopic ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}
                    data-testid={`topic-${t.id}`}
                  >
                    <div className="flex items-center gap-1 p-2">
                      <button
                        onClick={() => setExpanded((e) => ({ ...e, [t.id]: !open }))}
                        className="text-slate-400 hover:text-slate-900 w-5 text-center"
                        data-testid={`toggle-topic-${t.id}`}
                      >
                        {open ? "▼" : "▶"}
                      </button>
                      <span className="text-slate-400 tabular-nums text-[11px] w-5 text-center">{ti + 1}</span>
                      <button
                        onClick={() => setSelection({ kind: "topic", topicId: t.id })}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className="font-black text-sm text-slate-900 truncate">{t.title}</div>
                        <div className="text-[10px] text-slate-500">{(t.questions || []).length} otázek</div>
                      </button>
                      {!readOnly && (
                        <>
                          <button onClick={() => moveTopic(t.id, -1)} className="text-slate-400 hover:text-slate-900 text-xs px-1" title="Nahoru">↑</button>
                          <button onClick={() => moveTopic(t.id, 1)} className="text-slate-400 hover:text-slate-900 text-xs px-1" title="Dolů">↓</button>
                          <button
                            onClick={() => removeTopic(t.id)}
                            className="text-red-500 hover:text-red-700 text-xs px-1"
                            data-testid={`btn-remove-topic-${t.id}`}
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                    {open && (
                      <div className="pl-8 pr-2 pb-2 space-y-1">
                        {(t.questions || []).map((q, qi) => {
                          const selectedQ = selection?.kind === "question" && selection.questionId === q.id;
                          return (
                            <div
                              key={q.id}
                              className={`flex items-center gap-1 p-1.5 rounded-md text-sm ${
                                selectedQ ? "bg-blue-100 border border-blue-300" : "hover:bg-slate-50 border border-transparent"
                              }`}
                              data-testid={`question-${q.id}`}
                            >
                              <span className="text-slate-400 tabular-nums text-[11px] w-8 text-center">{ti + 1}.{qi + 1}</span>
                              <button
                                onClick={() => setSelection({ kind: "question", topicId: t.id, questionId: q.id })}
                                className="flex-1 min-w-0 text-left"
                              >
                                <div className="font-bold text-slate-900 truncate">{q.title}</div>
                                <div className="text-[10px] text-slate-500">{typeLabelOf(q.type)}</div>
                              </button>
                              {!readOnly && (
                                <>
                                  <button onClick={() => moveQuestion(t.id, q.id, -1)} className="text-slate-400 hover:text-slate-900 text-xs px-1" title="Nahoru">↑</button>
                                  <button onClick={() => moveQuestion(t.id, q.id, 1)} className="text-slate-400 hover:text-slate-900 text-xs px-1" title="Dolů">↓</button>
                                  <button
                                    onClick={() => removeQuestion(t.id, q.id)}
                                    className="text-red-500 hover:text-red-700 text-xs px-1"
                                    data-testid={`btn-remove-question-${q.id}`}
                                  >
                                    ✕
                                  </button>
                                </>
                              )}
                            </div>
                          );
                        })}
                        {!readOnly && (
                          <button
                            onClick={() => addQuestion(t.id)}
                            className="text-[11px] font-bold text-blue-600 hover:underline mt-1"
                            data-testid={`btn-add-question-${t.id}`}
                          >
                            + Přidat otázku do tématu
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {topics.length === 0 && (
                <div className="text-sm text-slate-400 text-center py-4">
                  Zatím žádná témata. {readOnly ? "" : "Klikni na + Téma."}
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Right: node editor */}
        <section className="bg-white border border-slate-200 rounded-lg p-5 min-h-[500px]">
          {currentNode ? (
            <>
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-slate-500">
                    {currentNode.kind === "topic" ? "Úprava tématu" : `Úprava otázky · ${currentNode.topic.title}`}
                  </div>
                  {currentNode.kind === "topic" ? (
                    <input
                      value={currentNode.obj.title || ""}
                      onChange={(e) => renameTopic(currentNode.topic.id, e.target.value)}
                      disabled={readOnly}
                      className="text-xl font-heading font-black tracking-tight text-slate-900 border-b-2 border-transparent focus:border-slate-900 focus:outline-none disabled:bg-transparent w-full"
                      data-testid="topic-title-input"
                    />
                  ) : (
                    <div className="font-heading font-black text-lg tracking-tight text-slate-900 truncate">{currentNode.obj.title}</div>
                  )}
                </div>
                <button
                  onClick={applyJson}
                  disabled={readOnly}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md px-4 py-2 text-sm disabled:opacity-40 flex-shrink-0"
                  data-testid="btn-apply-json"
                >
                  Aplikovat změny
                </button>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">JSON editor</label>
                  <textarea
                    value={jsonDraft}
                    onChange={(e) => setJsonDraft(e.target.value)}
                    disabled={readOnly}
                    className="mt-1 w-full border border-slate-200 rounded-md p-3 font-mono text-xs h-[600px] focus:outline-none focus:border-slate-900 disabled:bg-slate-50"
                    data-testid="json-editor"
                  />
                  {jsonError && <div className="mt-2 text-xs text-red-600 font-bold">{jsonError}</div>}
                </div>
                <div className="space-y-3 text-xs text-slate-600">
                  <div className="bg-slate-50 rounded-md p-3">
                    <div className="font-bold text-slate-900 mb-1">Tipy</div>
                    <ul className="list-disc pl-4 space-y-1">
                      {currentNode.kind === "topic" ? (
                        <>
                          <li>Téma musí mít <code>id</code>, <code>title</code> a <code>questions[]</code></li>
                          <li>Doplň <code>intro</code> a <code>lecturer_notes</code> pro metodiku</li>
                          <li>Pod tématem si můžeš přidat víc otázek</li>
                        </>
                      ) : (
                        <>
                          <li>ID otázky nelze měnit</li>
                          <li>Typ: {CHALLENGE_TYPES.map((t) => t.value).join(", ")}</li>
                          <li>Efekty: mood, aura, trust, safety, stress (−20…+20)</li>
                        </>
                      )}
                      <li>Po „Aplikovat" ještě klikni na „Uložit workshop"</li>
                    </ul>
                  </div>
                  {currentNode.kind === "topic" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <div className="font-bold text-slate-900 mb-1">Otázky v tomto tématu</div>
                      <div className="text-slate-700">
                        {(currentNode.obj.questions || []).length === 0
                          ? "Zatím žádné — přidej první."
                          : `${(currentNode.obj.questions || []).length} otázek`}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-slate-400 text-center py-20">
              Vyber téma nebo otázku vlevo{readOnly ? "" : ", nebo přidej nové"}.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Input({ label, value, onChange, disabled, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-slate-900 disabled:bg-slate-50"
      />
    </div>
  );
}

function Textarea({ label, value, onChange, disabled }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={3}
        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-slate-900 disabled:bg-slate-50"
      />
    </div>
  );
}

function typeLabelOf(t) {
  return CHALLENGE_TYPES.find((x) => x.value === t)?.label || t;
}
